package com.essntl.features.proposal.ui.proposalhistory.components

import androidx.compose.animation.AnimatedVisibility
import androidx.compose.foundation.background
import androidx.compose.foundation.clickable
import androidx.compose.foundation.interaction.MutableInteractionSource
import androidx.compose.foundation.layout.*
import androidx.compose.foundation.lazy.LazyColumn
import androidx.compose.foundation.lazy.itemsIndexed
import androidx.compose.foundation.shape.CircleShape
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.material3.HorizontalDivider
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.Text
import androidx.compose.runtime.*
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.clip
import androidx.compose.ui.graphics.TransformOrigin
import androidx.compose.ui.graphics.graphicsLayer
import androidx.compose.ui.input.pointer.PointerIcon
import androidx.compose.ui.input.pointer.pointerHoverIcon
import androidx.compose.ui.text.style.TextAlign
import androidx.compose.ui.unit.dp
import com.essntl.features.invoice.ui.allinvoice.InvoiceEvent
import com.essntl.features.invoice.ui.allinvoice.InvoiceState
import com.essntl.features.invoice.ui.allinvoice.components.InvoiceItem
import com.essntl.toastmanager.ToastManager
import com.essntl.uikit.components.button.primary.EsSmallPrimaryButton
import com.essntl.uikit.components.file.FileViewer
import com.essntl.uikit.components.icons.EsIcons
import com.essntl.uikit.components.icons.EsLargeIcon
import com.essntl.uikit.components.spinner.EsSpinner
import com.essntl.uikit.components.spinner.EsSpinnerSize
import com.essntl.uikit.components.utils.EsConstants
import com.essntl.uikit.components.utils.spacing
import com.essntl.uikit.theme.esColorScheme
import com.essntl.uikit.theme.esTypography
import kotlinx.coroutines.launch
import org.koin.compose.koinInject

@Composable
fun ProposalInvoices(
    state: InvoiceState,
    onInvoiceEvent: (InvoiceEvent) -> Unit,
    onInvoiceClick: (invoiceId: String) -> Unit,
    onAddInvoice: () -> Unit,
    proposalId: String,
    isAdmin: Boolean,
) {
    var isExpanded by remember(state.getAllQuickbooksInvoices) {
        mutableStateOf(
            !state.getAllQuickbooksInvoices.isLoading &&
                !state.getAllQuickbooksInvoices.data.isNullOrEmpty(),
        )
    }

    LaunchedEffect(Unit) {
        onInvoiceEvent(InvoiceEvent.GetInvoices(proposalId))
    }

    Column(
        modifier = Modifier
            .fillMaxWidth()
            .background(
                color = MaterialTheme.esColorScheme.bgSecondary,
                shape = RoundedCornerShape(EsConstants.RoundedCornerSize),
            )
            .padding(
                vertical = 2.spacing,
                horizontal = 2.5f.spacing,
            ),
    ) {
        Row(
            horizontalArrangement = Arrangement.SpaceBetween,
            verticalAlignment = Alignment.CenterVertically,
            modifier = Modifier.fillMaxWidth(),
        ) {
            Text(
                text = "Invoices",
                color = MaterialTheme.esColorScheme.textPrimary,
                style = MaterialTheme.esTypography.s2SemiBold16,
            )

            if (isAdmin)
                Row(
                    horizontalArrangement = Arrangement.spacedBy(.5.spacing),
                    verticalAlignment = Alignment.CenterVertically,
                ) {
                    EsSmallPrimaryButton(
                        text = "create new",
                        onClick = onAddInvoice,
                    )

                    if (state.getAllQuickbooksInvoices.isLoading)
                        EsSpinner(size = EsSpinnerSize.Medium)
                    else
                        EsLargeIcon(
                            res = EsIcons.ArrowDown,
                            modifier = Modifier
                                .graphicsLayer {
                                    transformOrigin = TransformOrigin.Center
                                    rotationZ = if (isExpanded) 180f else 0f
                                }
                                .clip(CircleShape)
                                .pointerHoverIcon(PointerIcon.Hand)
                                .clickable(
                                    interactionSource = remember { MutableInteractionSource() },
                                    indication = null,
                                ) {
                                    isExpanded = !isExpanded
                                },
                        )
                }
        }

        AnimatedVisibility(isExpanded) {
            Column(
                modifier = Modifier
                    .fillMaxWidth(),
            ) {
                Spacer(modifier = Modifier.height(1.spacing))

                LazyColumn(
                    horizontalAlignment = Alignment.CenterHorizontally,
                    verticalArrangement = Arrangement.spacedBy(.5.spacing),
                    modifier = Modifier.heightIn(max = 440.dp),
                ) {
                    when {
                        state.getAllQuickbooksInvoices.isFailure ->
                            item {
                                Box(
                                    contentAlignment = Alignment.CenterStart,
                                    modifier = Modifier
                                        .fillMaxSize()
                                        .padding(start = .5.spacing),
                                ) {
                                    Text(
                                        text = "Failed to load Invoices",
                                        textAlign = TextAlign.Start,
                                        style = MaterialTheme.esTypography.p1Regular16,
                                        color = MaterialTheme.esColorScheme.textPrimary,
                                        modifier = Modifier
                                            .align(Alignment.CenterStart),
                                    )
                                }
                            }

                        !state.getAllQuickbooksInvoices.isLoading &&
                            state.getAllQuickbooksInvoices.data.orEmpty().isEmpty() ->
                            item {
                                Box(
                                    contentAlignment = Alignment.CenterStart,
                                    modifier =
                                        Modifier
                                            .fillMaxSize()
                                            .padding(start = 0.5.spacing),
                                ) {
                                    Text(
                                        text = "No Invoices found!",
                                        textAlign = TextAlign.Start,
                                        style = MaterialTheme.esTypography.p1Regular16,
                                        color = MaterialTheme.esColorScheme.textPrimary,
                                        modifier = Modifier.align(Alignment.CenterStart),
                                    )
                                }
                            }

                        else ->
                            itemsIndexed(state.getAllQuickbooksInvoices.data.orEmpty()) { index, qbInvoice ->
                                key(qbInvoice.Id) {
                                    InvoiceItem(
                                        qbInvoice = qbInvoice,
                                        loadingDownloadQuickbookIds = state.loadingDownloadQuickbookIds,
                                        downloadQuickbooksInvoiceState = state.downloadQuickbooksInvoice,
                                        enabled = isAdmin,
                                        onDownloadInvoice = {
                                            onInvoiceEvent(InvoiceEvent.DownloadInvoice(qbInvoice))
                                        },
                                        onClick = {
                                            val invoiceId = state.getAllInvoices.data
                                                ?.find { it.qbId == qbInvoice.Id }
                                                ?.id
                                                ?: return@InvoiceItem

                                            onInvoiceClick(invoiceId)
                                        },
                                    )

                                    if (index != state.getAllQuickbooksInvoices.data.orEmpty().lastIndex) {
                                        Spacer(modifier = Modifier.height(.5.spacing))

                                        HorizontalDivider(
                                            color = MaterialTheme.esColorScheme.borderPrimary,
                                        )
                                    }
                                }
                            }
                    }

                    if (state.getAllQuickbooksInvoices.isLoading)
                        item {
                            Box(
                                contentAlignment = Alignment.Center,
                                modifier = Modifier.fillMaxSize(),
                            ) {
                                EsSpinner(
                                    color = MaterialTheme.esColorScheme.iconPrimary,
                                    size = EsSpinnerSize.Large,
                                    modifier = Modifier
                                        .align(Alignment.Center),
                                )
                            }
                        }
                }
            }
        }
    }

    val scope = rememberCoroutineScope()
    val toastManager = koinInject<ToastManager>()

    FileViewer(
        fileUrl = state.downloadQuickbooksInvoice.data?.first.orEmpty(),
        fileBaseName = state.downloadQuickbooksInvoice.data?.second?.DocNumber?.let {
            "invoice_$it"
        },
        extension = "pdf",
        isDialogOpened = state.downloadQuickbooksInvoice.isSuccess,
        onDismissRequest = {
            onInvoiceEvent(InvoiceEvent.ClearDownloadInvoice)
        },
        onDownloadSuccess = {
            val invoiceDocNumber =
                state.downloadQuickbooksInvoice.data?.second?.DocNumber?.let {
                    " $it"
                }

            scope.launch {
                toastManager.success(description = "Invoice $invoiceDocNumber downloaded!")
            }
        },
    )
}